import SideModal from "components/SideModal";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { editSummon } from "services/prosecutorDecipilineService";
import { Complaints, Investigation, Summon } from "types";
import { LoadingIcon } from "utils/constant";
import { SUMMON_STATUS } from "utils/constantValues";
import "../styles/AddVictimModal.css";
import { useAuth } from "contexts/AuthContext";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  summonData: Summon;
  complaintData: Complaints;
  investigationData: Investigation;
  onSummonRefresh: any;
}

export default function AddLetterModal({
  isOpen,
  setIsOpen,
  summonData,
  complaintData,
  investigationData,
  onSummonRefresh,
}: ModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const summonFileInputRef = useRef<HTMLInputElement>(null);

  const [formState, setFormState] = useState({
    hearing_date: "",
    region: "",
    zone: "",
    woreda: "",
    kebele: "",
    summary: "",
    hasSummon: true,
    files: [] as File[],
    summonFiles: [] as File[],
    investigation: null,
  });

  const [stampUrl, setStampUrl] = useState<string | null>(null);
  const [stampBlob, setStampBlob] = useState<Blob | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;

    const loadStamp = async () => {
      try {
        // Construct the actual API URL – adjust to your real endpoint
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL_2}organization/service-stamp/read_stamp/?admin_unit_uuid=${user.admin_unit}&service_name=pdms`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            }, // if needed
          },
        );
        if (!response.ok) throw new Error("Failed to fetch stamp");
        const blob = await response.blob(); // ✅ direct binary, no string corruption
        setStampBlob(blob);
        objectUrl = URL.createObjectURL(blob);
        setStampUrl(objectUrl);
      } catch (error) {
        console.error("Failed to load stamp", error);
      }
    };

    if (isOpen && user) loadStamp();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isOpen, user]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  // const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
  //   if (e.target.files) {
  //     setFormState((prev) => ({
  //       ...prev,
  //       files: [...Array.from(e.target.files as FileList)],
  //     }));
  //   }
  // };

  const handleSummonFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        summonFiles: [...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!investigationData?.uu_id) {
      showToast(t("investigationNotFound"), "error");
      return;
    }
    if (!stampBlob) {
      showToast("Stamp is required.", "error");
      return;
    }

    // if (formState?.files?.length <= 0) {
    //   showToast(t("letterFileRequired"), "error");
    //   return;
    // }
    setLoading(true);

    const data = new FormData();
    // data.append("letter_upload_files", formState?.files[0] || "");
    data.append("status", SUMMON_STATUS.FORWAREDED_TO_ACCUSED);
    data.append("investigation", investigationData?.uu_id);
    data.append(
      "accused_prosecutor_name",
      `${complaintData?.accused_prosecutor_name || ""}`,
    );
    data.append(
      "user_id_accused_prosecutor_id",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "assigned_prosecutor",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "user_id_assigned_prosecutor",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "accused_prosecutor_name",
      complaintData?.accused_prosecutor_name || "",
    );
    data.append(
      "user_id_accused_prosecutor_name",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "accused_prosecutor_org_id",
      complaintData?.accused_prosecutor_org_id || "",
    );
    data.append(
      "accused_prosecutor_admin_unit_id",
      complaintData?.accused_prosecutor_admin_unit_id || "",
    );
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");

    data.append("hearing_date", formState?.hearing_date);
    data.append("region", formState?.region);
    data.append("zone", formState?.zone);
    data.append("woreda", formState?.woreda);
    data.append("kebele", formState?.kebele);
    data.append("summary", formState?.summary);
    data.append("summon_file_files", formState?.summonFiles[0] || "");
    data.append("investigation", investigationData?.uu_id);
    data.append("status", SUMMON_STATUS.SUMMON_ISSUED);
    data.append("signature1", stampBlob, "stamp.png");

    const isFormData = true;
    const summon_id = summonData?.id || "";

    try {
      // await addSummon(data, isFormData);
      await editSummon(data, summon_id, isFormData);

      await Promise.resolve(onSummonRefresh?.());
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      setFormState({
        hasSummon: false,
        hearing_date: "",
        region: "",
        zone: "",
        woreda: "",
        kebele: "",
        summary: "",
        files: [] as File[],
        summonFiles: [] as File[],
        investigation: null,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  // const triggerFileInput = () => {
  //   fileInputRef.current?.click();
  // };

  const triggerSummonFileInput = () => {
    summonFileInputRef.current?.click();
  };

  if (!isOpen) return null;
  return (
    <SideModal isOpen={isOpen} setIsOpen={setIsOpen} title={t("createLetter")}>
      <form onSubmit={handleSubmit} className="victim-form">
        {/* <section className="evidence-section">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("letterDocument")}
          </label>
          <div className="file-upload-area" onClick={triggerFileInput}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <p className="upload-text">{t("uploadFiles")}</p>
            {formState?.files && formState?.files?.length > 0 && (
              <div className="file-list">
                {formState?.files?.map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section> */}

        {/* Summon options */}
        {/* keep hasSummon toggle consistent with advocate UI */}
        {/* <div className="mb-4">
          <div className="flex items-center gap-2 bg-gray-100 border p-2 rounded-md">
            <input
              className="form-checkbox h-4 w-4 text-blue-600"
              type="checkbox"
              name="hasSummon"
              checked={formState.hasSummon}
              onChange={(e) => setFormState((prev) => ({ ...prev, hasSummon: !prev.hasSummon }))}
            />
            <span className="text-sm text-gray-700">{t("sendSummonToo")}</span>
          </div>
        </div> */}

        <section className="victim-section">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("hearingDate")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type={"date"}
              name="hearing_date"
              value={formState.hearing_date}
              onChange={handleChange}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("region")}
              </label>
              <input
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="text"
                name="region"
                value={formState.region}
                onChange={handleChange}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("zone")}
              </label>
              <input
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="text"
                name="zone"
                value={formState.zone}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("woreda")}
              </label>
              <input
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="text"
                name="woreda"
                value={formState.woreda}
                onChange={handleChange}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("kebele")}
              </label>
              <input
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="text"
                name="kebele"
                value={formState.kebele}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("summary")}
            </label>
            <textarea
              required
              rows={3}
              name="summary"
              value={formState.summary}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>
        </section>

        <section className="evidence-section">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("summonFile")}
          </label>
          <div className="file-upload-area" onClick={triggerSummonFileInput}>
            <input
              type="file"
              ref={summonFileInputRef}
              onChange={handleSummonFileChange}
              style={{ display: "none" }}
            />
            <p className="upload-text">{t("uploadFiles")}</p>
            {formState?.summonFiles && formState?.summonFiles?.length > 0 && (
              <div className="file-list">
                {formState?.summonFiles?.map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {stampUrl && (
          <div className="my-4 flex items-center justify-center">
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3 shadow-sm">
              <svg
                className="w-6 h-6 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-green-700 font-semibold">
                Stamp fetched successfully
              </span>
            </div>
          </div>
        )}

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon} {t("submitting")}
            </div>
          ) : (
            t("createLetter")
          )}
        </button>
      </form>
    </SideModal>
  );
}
